// Mesajları çekerken çöz (9.34 · D-611, D-612).
//
// Sohbet ekranı mesajları ALTI ayrı yerden çekiyor. Her birine ayrı bir çözme
// adımı eklemek, bir gün birinin unutulacağı anlamına gelirdi — ve unutulan yer
// kullanıcıya şifreli metin gösterirdi. Bu yüzden çözme, çağrı yerlerine değil
// ÇEKME İŞİNİN KENDİSİNE bağlanıyor: `api.getMessages` yerine bu kullanılır.
//
// D-612: çözme "sohbet açılınca" değil, mesaj uygulamaya ULAŞTIĞINDA olur.
import { api, Message } from '../services/api';
import { resolveIncoming } from './plaintextCache';
import { myDeviceId } from './session';
import { identityChanges, identitiesSeen } from './identityWatch';
import { cleanLinkPreview, decodeMessagePayload } from '../linkPreview/payload';

// Kimlik değişikliği satırının mesaj kimliği. Sunucudan gelen mesajlarla
// çakışmaması gerekiyor: ikisi ayrı dizilerden geliyor ve çakışan bir kimlik,
// tepkinin ya da alıntının yanlış satıra yazılması demek olurdu (D-285'in
// çağrı satırlarında düzelttiği kusurun aynısı). Negatif alan yalnız bu
// satırlara ait.
const IDENTITY_ROW_BASE = -1_000_000;

/**
 * 9.32 / D-658 — şifresiz mesajın önizlemesi sunucudan, ayrı bir alanda gelir.
 * Karşı taraftan geldiği için güvenilmez girdidir: şifreli mesajın içindekiyle
 * AYNI süzgeçten geçer (görsel yalnız `data:`, link metinde yazan link). Süzgeçten
 * geçmeyen önizleme düşer; mesaj açık adresiyle kalır.
 */
function withCleanPreview(message: Message): Message {
  if (message.link_preview === undefined) return message;
  const { link_preview: raw, ...rest } = message;
  const link = message.type === 'text' && raw ? cleanLinkPreview(raw, message.content) : null;
  return link ? { ...rest, link_preview: link } : rest;
}

/**
 * Şifreli satırları düz metne çevir. Çözülen mesaj `text` türüne dönüşür, yani
 * çizim katmanı hiçbir şey bilmek zorunda kalmaz. Çözülemeyen satır `encrypted`
 * KALIR — arayüz orada "okunamıyor" gösterir; sessizce boş bir balon çizmek
 * yalan olurdu.
 *
 * Kendi gönderdiğimiz mesajlar da şifreli gelir ve onları çözemeyiz (gönderme
 * zinciri ayrıdır); onlar gönderim anında yazılan önbellekten gelir (D-611).
 */
export async function decryptMessages(peerId: number, messages: Message[]): Promise<Message[]> {
  const out: Message[] = [];
  // Sıra korunuyor: zincirin doğal sırası budur. Sırasız gelse de ratchet
  // atlanan anahtarlarla başa çıkar, ama gereksiz yere atlatmıyoruz.
  for (const message of messages) {
    if (message.type === 'image' && message.caption_encrypted) {
      const resolved = await resolveIncoming(peerId, message.id, message.caption ?? '', 0);
      out.push({ ...withCleanPreview(message),
        caption: resolved.ok ? decodeMessagePayload(resolved.text).text : null,
        caption_unreadable: !resolved.ok });
      continue;
    }
    if (message.type !== 'encrypted') {
      out.push(withCleanPreview(message));
      continue;
    }
    // 9.32 / D-658 — şifreli mesajın tek geçerli önizlemesi zarfın İÇİNDEKİDİR.
    // Satırın açık alanı burada hep boş olmalı; doluysa (sunucu ya da araya
    // giren biri koymuşsa) atılır: şifreli bir mesaja dışarıdan sahte bir
    // başlık ve alan adı iliştirilemesin.
    const base = { ...message, encrypted_origin: true };
    delete base.link_preview;
    const resolved = await resolveIncoming(peerId, message.id, message.content, message.edit_revision ?? 0);
    if (!resolved.ok) {
      out.push(base);
      continue;
    }
    // 9.32 / D-653 — şifrenin içinde metnin yanında link önizlemesi olabilir.
    const payload = decodeMessagePayload(resolved.text);
    out.push(payload.link
      ? { ...base, content: payload.text, type: 'text', link_preview: payload.link }
      : { ...base, content: payload.text, type: 'text' });
  }
  return out;
}

/**
 * `api.getMessages` + çözme. Sohbet ekranı bunu kullanır.
 *
 * Cihaz numarası sunucuya geçiriliyor (9.34 · D-624): fan-out sonrası her cihaz
 * KENDİ zarfını almalı. Numara yoksa sunucu bugünkü tek alanı veriyor.
 */
export async function getMessages(
  token: string,
  peerId: number,
  // 9.34 / D-634 — sohbet AÇILIRKEN kimlik denetimi. Her yoklamada değil: dizin
  // listesi ücretsiz ama bedava değil, 5 saniyede bir sormak istek sayısını
  // gereksiz yere artırırdı. Değişikliği kaçırmıyoruz — kaçırsak bile bir
  // sonraki gönderim aynı denetimi yapıyor.
  kimlikDenetle = false,
): Promise<Message[]> {
  if (kimlikDenetle) await denetle(token, peerId);
  const cozulmus = await decryptMessages(peerId, await api.getMessages(token, peerId, await myDeviceId()));
  return withIdentityChanges(cozulmus, await identityChanges(peerId));
}

/**
 * Kişinin BÜTÜN cihaz kimliklerini dizinden al ve bilinenlerle karşılaştır.
 *
 * Neden tam liste: tek anahtara bakmak yanlış alarm üretirdi. Karşı tarafın
 * ikinci telefonundan gelen ilk mesaj da "daha önce görmediğim anahtar"dır,
 * oysa ortada bir değişiklik yoktur. Denetim ancak elde kişinin tamamı varken
 * doğru sonuç verir; o yüzden yalnız listeyi aldığımız yerlerde yapılıyor.
 *
 * Hata sohbeti ENGELLEMİYOR: dizine ulaşılamazsa mesajlar yine gelir. Uyarı
 * vermemek kötü, mesajı hiç göstermemek daha kötü.
 */
async function denetle(token: string, peerId: number): Promise<void> {
  try {
    const liste = await api.fetchDeviceList(token, peerId);
    await identitiesSeen(peerId, liste.devices.map((d) => d.identity_key));
  } catch {
    // Karşı taraf hiç anahtar yayımlamamış olabilir (404) ya da ağ düşmüş
    // olabilir; ikisi de sohbeti durdurmaz.
  }
}

/**
 * Kimlik değişikliği satırlarını mesaj akışına yerleştir (9.34 · D-634).
 *
 * Uyarı sohbetin İÇİNDE ve OLDUĞU ANDA duruyor: ekranın tepesindeki kalıcı bir
 * şerit "ne zaman oldu, hangi mesajlardan sonra" sorusunu yanıtlamaz — oysa
 * insanın bilmek istediği tam olarak budur.
 *
 * Satır sunucudan gelmiyor; sunucuda karşılığı olamaz, çünkü "bu cihaz daha
 * önce ne gördü" sorusunu yalnız cihaz yanıtlayabilir. Bu yüzden çağrı
 * satırları gibi OKUMA ANINDA karışıyor.
 */
export function withIdentityChanges(
  messages: Message[],
  changes: { at: string }[],
): Message[] {
  if (changes.length === 0) return messages;
  const satirlar: Message[] = changes.map((c, i) => ({
    id: IDENTITY_ROW_BASE - i,
    sender_id: 0,
    content: '',
    type: 'identity_change',
    status: 'sent',
    created_at: c.at,
  }));
  return [...messages, ...satirlar].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}
